import { ICopyProps } from "actions/collections";
import {
    IAttributeFilters,
    IDimensionFilter,
} from "components/product-collection/product-collection-toolbar/product-collection-toolbar-filters/types";
import { getAttributesDoorsFilter } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { COLLECTIONS } from "constants/action-types";
import {
    ALLOCATION_DIMENSION_ID_PREFIX,
    ExportCollectionUnitType,
    ExportLayout,
    ExportMerchant,
    ExportRequestType,
} from "constants/product-collection";
import {
    AllocationDimensionIndex,
    AllocationDimensionKind,
    getAllocationDimensionByIndex,
    IAllocationDimension,
} from "helpers/allocation-dimension";
import { getAttributeFilters, getDimensionFilters, IDoor } from "helpers/door-collection";
import { ISchemaKey, SchemaKeyMap } from "helpers/product-collection";
import { List, Map } from "immutable";
import { ParentFieldsMap } from "reducers/collection";
import { getAllocationDimensionsBySubtype, getAllocationDimensionsBySubtypeRaw } from "reducers/selectors/collection";
import { difference } from "lodash";
import { IStore } from "reducers";
import { IServerRollupRequest } from "helpers/query-dsl-types";
import { getGridParamsFromLocation } from "components/product-collection/product-collection";
import { EXPORT_ASSORTMENT_TO_MERCHANT, EXPORT_ASSORTMENT, EXPORT_ROLLUP } from "constants/analytics";
import { AssortmentTemplate } from "containers/folders/types/base";
import { Location } from "history";

export const getDimensionFiltersForExport = (
    dimensionFilters: IDimensionFilter[],
    allocationDimensions: IAllocationDimension[],
) => {
    return dimensionFilters.map((values, i) => {
        return {
            dimension_id: allocationDimensions[i].id.replace(ALLOCATION_DIMENSION_ID_PREFIX, ""),
            dimension_values: values,
        };
    });
};

export const getAttributeFiltersForExport = (
    doors: IDoor[],
    attributeFilters: IAttributeFilters,
    allocationDimensions: IAllocationDimension[],
) => {
    const attributeDoorsFilters = getAttributesDoorsFilter(doors, attributeFilters);
    const doorDimension = getAllocationDimensionByIndex(allocationDimensions, AllocationDimensionIndex.Door);

    return Object.keys(attributeFilters || {}).map((key) => {
        return {
            dimension_id: doorDimension.id.replace(ALLOCATION_DIMENSION_ID_PREFIX, ""),
            dimension_values: attributeDoorsFilters[key],
        };
    });
};

export const getAttributeFiltersForRollupExport = (doors: IDoor[], attributeFilters: IAttributeFilters) => {
    const attributeDoorsFilters = getAttributesDoorsFilter(doors, attributeFilters);

    return Object.keys(attributeFilters || {}).map((key) => {
        return {
            name: key,
            doors: attributeDoorsFilters[key],
        };
    });
};

type RowMeta = {
    rowId: string;
    rowIndex: number;
};

export const makeAutoSelectSingleResultEpicActions = (
    updatedField: string,
    dependentFieldsLookupMap: ParentFieldsMap,
    options: RowMeta,
) =>
    dependentFieldsLookupMap.get(updatedField, []).map((dependentColumnId) => ({
        type: COLLECTIONS.COLLECTION.ITEMS.ITEM.SYNC_AUTO_SELECT_SINGLE_RESULT,
        payload: {
            dependentColumnId,
            ...options,
        },
    }));

const getAutoSelectSingleResult = (schemaKey: SchemaKeyMap) =>
    schemaKey.getIn(["lookup_plan", "auto_select_single_result"]);

const getDependentFields = (schemaKey: SchemaKeyMap) => schemaKey.getIn(["lookup_plan", "where", "fields"]);

export const makeDependentFieldsMapForLookup = (schemaList: List<Map<keyof ISchemaKey, any>>): ParentFieldsMap => {
    const schemaKeysWithAutoSelectSingleResult = schemaList.filter(getAutoSelectSingleResult);
    return schemaKeysWithAutoSelectSingleResult.reduce<ParentFieldsMap>((state, schemaKeyWithAutoSelect) => {
        const parentColumns = getDependentFields(schemaKeyWithAutoSelect);
        const filteredColumns = parentColumns.filter(
            (column) => column.getIn(["is_required"]) && !column.getIn(["column", "enforced_value"]),
        );

        return filteredColumns.reduce(
            (prevState, column) =>
                prevState.update(column.getIn(["column", "schema_column"]), [], (arrayOfDependentFields) =>
                    arrayOfDependentFields.concat([schemaKeyWithAutoSelect.get("field_name")]),
                ),
            state,
        );
    }, Map());
};

interface IDimensionValue {
    allocation_dimension_id: string;
    dimension_value_ids: string[];
}
export interface ICopyCollectionRequestBody {
    name: string;
    brand_id: string;
    collection_id: string;
    collection_type: string;
    with_items: boolean;
    with_permissions: boolean;
    without_door_labels: boolean;
    with_sizing_allocations: boolean;
    with_plans: boolean;
    with_purchase_orders: boolean;
    with_plan_targets: boolean;
    merchant_id: string;
    parent_id: string;
    dimension_values: IDimensionValue[];
    bag_version: number;
    with_default_row: boolean;
    target_merchant_id?: string;
}
export const prepareRequestBodyForCopyCollection = (params: ICopyProps, state: IStore): ICopyCollectionRequestBody => {
    const {
        name,
        brandId,
        collectionId,
        collectionType,
        withItems,
        withPermissions,
        withDefaultRow,
        withDoorLabels,
        allocationData,
        withPODetails,
        withTargetData,
        planData,
        excludedAllocationDimensionValues,
        allocationDimensionValues,
        parentId,
        merchantId,
        targetMerchantId,
        bagVersion,
        collectionPoiId,
        collectionAllocationDimensions,
    } = params;
    const genericAllocationDimensions = collectionAllocationDimensions
        ? getAllocationDimensionsBySubtypeRaw(collectionAllocationDimensions, AllocationDimensionKind.Generic)
        : getAllocationDimensionsBySubtype(state, collectionId, AllocationDimensionKind.Generic);

    const deliveriesAllocationDimension = getAllocationDimensionByIndex(
        genericAllocationDimensions,
        AllocationDimensionIndex.Date,
    );
    const doorsAllocationDimension = getAllocationDimensionByIndex(
        genericAllocationDimensions,
        AllocationDimensionIndex.Door,
    );
    const dimensionValues: IDimensionValue[] = [];

    [deliveriesAllocationDimension, doorsAllocationDimension].forEach((dimension) => {
        if (dimension && ["id", "dimension_values"].every((key) => key in dimension)) {
            const allocationDimensionId = dimension.id.replace(ALLOCATION_DIMENSION_ID_PREFIX, "");
            dimensionValues.push({
                dimension_value_ids: difference(
                    allocationDimensionValues[allocationDimensionId] ?? [],
                    excludedAllocationDimensionValues[allocationDimensionId] ?? [],
                ),
                allocation_dimension_id: allocationDimensionId,
            });
        }
    });

    const body = {
        name,
        brand_id: brandId,
        collection_id: collectionId,
        collection_type: collectionType,
        with_items: withItems,
        with_permissions: withPermissions,
        without_door_labels: !withDoorLabels,
        with_sizing_allocations: allocationData,
        with_plans: planData,
        with_purchase_orders: withPODetails,
        with_plan_targets: withTargetData,
        merchant_id: merchantId || state.collection.merchantId,
        parent_id: parentId,
        dimension_values: dimensionValues,
        bag_version: bagVersion || 0,
        with_default_row: withDefaultRow,
        collection_poi_id: collectionPoiId,
    };

    if (targetMerchantId) {
        return {
            ...body,
            target_merchant_id: targetMerchantId,
        };
    }

    return body;
};

export const trackEventExportRollup = ({ analytics, merchantName, numberOfRows }) => {
    analytics.track(EXPORT_ROLLUP, {
        merchantName,
        type: ExportRequestType.Excel,
        numberOfRows,
    });
};

export const trackEventExportAssortment = ({
    analytics,
    merchantName,
    layout = ExportLayout.Vertical,
    unitType,
    selectedAllocationDimensions,
    horizontalAllocationDimensions,
    showExportToMerchant,
    view,
    accountType,
    allocationDimensions,
}) => {
    const selectedAllocationDimensionNames = selectedAllocationDimensions.reduce(
        (acc, selectedAllocationDimensionId) => {
            const selectedAllocationDimension = allocationDimensions.find(
                (allocationDimension) =>
                    allocationDimension.id.replace(ALLOCATION_DIMENSION_ID_PREFIX, "") ===
                    selectedAllocationDimensionId,
            );
            if (selectedAllocationDimension) {
                acc.push(selectedAllocationDimension.name);
            }

            return acc;
        },
        [],
    );
    const horizontalAllocationDimension = allocationDimensions.find(
        (allocationDimension) =>
            allocationDimension.id.replace(ALLOCATION_DIMENSION_ID_PREFIX, "") === horizontalAllocationDimensions,
    );

    analytics.track(showExportToMerchant ? EXPORT_ASSORTMENT_TO_MERCHANT : EXPORT_ASSORTMENT, {
        merchantName,
        type: unitType === ExportCollectionUnitType.Actual ? ExportRequestType.Csv : ExportRequestType.Excel,
        layout,
        dimensions: selectedAllocationDimensionNames,
        horizontal_dimension: horizontalAllocationDimension ? horizontalAllocationDimension.name : undefined,
        view,
        userType: accountType,
        template:
            unitType === ExportCollectionUnitType.Inventory ? AssortmentTemplate.Closeout : AssortmentTemplate.Standard,
    });
};

export interface IPrepareExportAssortmentRequest {
    type?: ExportRequestType;
    visibleColumnsList?: string[];
    visibleRowsList?: string[];
    exportFileName?: string;
    layout?: ExportLayout;
    withUnits: boolean;
    withNotes: boolean;
    unitType: ExportCollectionUnitType;
    selectedAllocationDimensions: string[];
    horizontalAllocationDimensions: string;
    rowOrder?: { field: string; order: string };
    actionMessage?: ExportMerchant | undefined;
    includeAssortmentId?: boolean;
    merchantId: string;
    allocationDimensions: IAllocationDimension[];
    locationBeforeTransitions?: Location;
    doors: IDoor[];
    isUpcExporting?: boolean;
}

export interface IExportAssortmentRequest {
    type: ExportRequestType;
    data_layout: ExportLayout;
    include_units: boolean;
    include_door_notes: boolean;
    unit_type: ExportCollectionUnitType;
    selected_allocation_dimensions: string[];
    horizontal_allocation_dimension: string;
    merchant_id: string;
    row_order?: {
        field: string;
        order: string;
    };
    action_message?: ExportMerchant | undefined;
    include_assortment_id?: boolean;
    export_name?: string;
    row_filter?: {
        in: {
            field: string;
            values: { string_value: string }[];
        };
    };
    projections?: string[];
    dimension_values?: any;
    attribute_filters?: any;
    include_upc?: boolean;
}

export const prepareRequestBodyForExportAssortment = ({
    type = ExportRequestType.Excel,
    visibleColumnsList = [],
    visibleRowsList = [],
    exportFileName,
    layout = ExportLayout.Vertical,
    withUnits,
    withNotes,
    unitType,
    selectedAllocationDimensions,
    horizontalAllocationDimensions,
    rowOrder,
    actionMessage,
    includeAssortmentId,
    merchantId,
    allocationDimensions,
    locationBeforeTransitions,
    doors = [],
    isUpcExporting,
}: IPrepareExportAssortmentRequest): IExportAssortmentRequest => {
    const body: IExportAssortmentRequest = {
        type,
        data_layout: layout,
        include_units: withUnits,
        include_door_notes: withNotes,
        unit_type: unitType,
        selected_allocation_dimensions: selectedAllocationDimensions,
        horizontal_allocation_dimension: horizontalAllocationDimensions,
        merchant_id: merchantId,
        include_upc: isUpcExporting,
    };

    if (rowOrder) {
        body.row_order = rowOrder;
    }

    if (actionMessage) {
        body.action_message = actionMessage;
    }

    if (includeAssortmentId) {
        body.include_assortment_id = true;
    }

    body.export_name = exportFileName;

    if (visibleRowsList.length > 0) {
        const convertedRows = visibleRowsList.map((rowId) => ({ string_value: rowId }));
        body.row_filter = {
            in: {
                field: "id",
                values: convertedRows,
            },
        };
    }

    if (visibleColumnsList.length > 0) {
        body.projections = visibleColumnsList;
    }

    if (locationBeforeTransitions) {
        const gridParams = getGridParamsFromLocation(locationBeforeTransitions);
        const dimensionFilters = getDimensionFilters(gridParams);
        const attributeFilters = getAttributeFilters(gridParams);
        const hasDimensionFilters = dimensionFilters.length > 0;
        const hasAttributesfilters = Object.keys(attributeFilters || {}).length > 0;

        if (hasDimensionFilters) {
            body.dimension_values = getDimensionFiltersForExport(dimensionFilters, allocationDimensions);
        }

        if (hasAttributesfilters) {
            body.attribute_filters = getAttributeFiltersForExport(doors, attributeFilters, allocationDimensions);
        }
    }

    return body;
};

export interface IPrepareExportRollupRequest {
    merchantId: string;
    queryFilter?: IServerRollupRequest;
    visibleColumnsList: string[];
    selectedAllocationDimensions: string[];
    allocationDimensions: IAllocationDimension[];
    doors: IDoor[];
    locationBeforeTransitions?: Location;
}
export interface IExportRollupRequest {
    type: ExportRequestType;
    merchant_id: string;
    selected_allocation_dimensions: string[];
    projections?: string[];
    delivery_dimension_id?: string;
    door_dimension_id?: string;
    attribute_filter?: any;
}
export const prepareRequestBodyForExportRollup = ({
    merchantId,
    queryFilter,
    visibleColumnsList,
    selectedAllocationDimensions,
    allocationDimensions,
    doors,
    locationBeforeTransitions,
}: IPrepareExportRollupRequest): IExportRollupRequest => {
    const { filter: row_filter, delivery_filter, door_filter, search } = (queryFilter || {}) as IServerRollupRequest;

    const body: IExportRollupRequest = {
        type: ExportRequestType.Excel,
        merchant_id: merchantId,
        selected_allocation_dimensions: selectedAllocationDimensions ?? [],
        projections: visibleColumnsList,
        ...(queryFilter && { row_filter, delivery_filter, door_filter, search }),
    };
    const doorDimension = getAllocationDimensionByIndex(allocationDimensions, AllocationDimensionIndex.Door);
    const deliveryDimension = getAllocationDimensionByIndex(allocationDimensions, AllocationDimensionIndex.Date);

    if (deliveryDimension?.id) {
        body.delivery_dimension_id = deliveryDimension.id.replace(ALLOCATION_DIMENSION_ID_PREFIX, "");
    }

    if (doorDimension?.id) {
        body.door_dimension_id = doorDimension.id.replace(ALLOCATION_DIMENSION_ID_PREFIX, "");
    }

    if (locationBeforeTransitions) {
        const gridParams = getGridParamsFromLocation(locationBeforeTransitions);
        const attributeFilters = getAttributeFilters(gridParams);
        const hasAttributesfilters = Object.keys(attributeFilters || {}).length > 0;

        if (hasAttributesfilters) {
            body.attribute_filter = getAttributeFiltersForRollupExport(doors, attributeFilters);
        }
    }

    return body;
};

export const getColumnsForExportUpcs = (visibleColumnsList: string[], merchantIdentifierFields: string[]): string[] => {
    return Array.from(new Set(visibleColumnsList.concat(merchantIdentifierFields))) as string[];
};

export const isAllocationDimensionsObjectId = (objectId: string) =>
    new RegExp(ALLOCATION_DIMENSION_ID_PREFIX, "i").test(objectId);
